import { useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Download, Users } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Checkbox } from '@quebon/ui/components/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import { Field, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Spinner } from '@quebon/ui/components/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@quebon/ui/components/table'
import { Textarea } from '@quebon/ui/components/textarea'
import { handleApiError } from '@/api/client'
import {
  briefingApplicationApi,
  briefingSessionApi,
  type BriefingApplicationListItem,
  type BriefingApplicationUpdatePayload,
  type BriefingAttendanceStatus,
  type BriefingSessionAdmin,
  type BriefingStatus,
  type SessionMode,
} from '@/api/lcMarketing'
import { ErrorBlock, FilterSelect, LabeledSelect, LoadingBlock, Pagination } from '../../crm/crmComponents'
import { FRANCHISE_TYPE_LABELS, INTEREST_LABELS, SESSION_MODE_LABELS } from './inquiryLabels'
import { BriefingSessionFilter } from './BriefingSessionFilter'
import { BriefingAttendanceDialog } from './BriefingAttendanceDialog'
import { useSidoList, useSigunguList, type RegcodeEntry } from './useRegcode'

const STATUS_LABEL: Record<BriefingStatus, string> = {
  pending: '대기중', contacted: '연락완료', done: '종결', duplicate: '중복접수',
}
const ATTENDANCE_LABEL: Record<BriefingAttendanceStatus, string> = {
  ATTENDED: '참석', NO_SHOW: '불참', UNKNOWN: '미기록',
}
const PAGE_SIZE = 20

export default function BriefingApplicationsTab() {
  const [params, setParams] = useSearchParams()
  const sessionId = params.get('sessionId') ?? ''
  const [status, setStatus] = useState<BriefingStatus | ''>('')
  const [sessionMode, setSessionMode] = useState<SessionMode | ''>('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<BriefingApplicationListItem | null>(null)
  const [attendanceOpen, setAttendanceOpen] = useState(false)
  const client = useQueryClient()
  const listParams = { status: status || undefined, sessionMode: sessionMode || undefined, sessionId: sessionId || undefined, search: search || undefined }
  const list = useQuery({
    queryKey: ['briefing-applications', { ...listParams, page }],
    queryFn: () => briefingApplicationApi.list({ ...listParams, page, size: PAGE_SIZE }),
  })
  const sessions = useQuery({
    queryKey: ['briefing-sessions-admin', 'all'],
    queryFn: () => briefingSessionApi.list(),
  })
  const currentSession = sessions.data?.find((session) => session.sessionId === sessionId)
  const openSessions = sessions.data?.filter((session) => session.applicationAvailability === 'open') ?? []
  const refresh = () => {
    for (const queryKey of [
      ['briefing-applications'], ['briefing-sessions-admin'], ['briefing-attendance'],
      ['lc-crm-inbox-customers'], ['lc-crm-inbox-customer-detail'], ['lc-crm-intake-detail'],
      ['lc-crm-dashboard'], ['lc-crm-funnel'],
    ]) client.invalidateQueries({ queryKey })
  }
  const changeSession = (next: string) => {
    const nextParams = new URLSearchParams(params)
    if (next) nextParams.set('sessionId', next)
    else nextParams.delete('sessionId')
    setParams(nextParams, { replace: true })
    setPage(0)
  }
  const update = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: BriefingApplicationUpdatePayload }) => briefingApplicationApi.update(id, payload),
    onSuccess: () => { refresh(); setSelected(null); toast.success('변경사항이 저장되었습니다.') },
    onError: (error) => toast.error(handleApiError(error) || '저장에 실패했습니다.'),
  })
  const processing = useMutation({
    mutationFn: ({ id, next }: { id: number; next: BriefingStatus }) => briefingApplicationApi.updateStatus(id, next),
    onSuccess: (_, { next }) => { refresh(); setSelected((current) => current ? { ...current, status: next } : null) },
    onError: (error) => toast.error(handleApiError(error) || '처리 상태 변경에 실패했습니다.'),
  })
  const participation = useMutation({
    mutationFn: ({ id, targetSessionId }: { id: number; targetSessionId?: string }) => targetSessionId ? briefingApplicationApi.move(id, targetSessionId) : briefingApplicationApi.cancelParticipation(id),
    onSuccess: () => { refresh(); setSelected(null); toast.success('참석 신청 상태를 변경했습니다.') },
    onError: (error) => toast.error(handleApiError(error) || '정원·중복·마감 상태를 확인해주세요.'),
  })
  const attendance = useMutation({
    mutationFn: ({ id, next }: { id: number; next: BriefingAttendanceStatus }) => briefingApplicationApi.updateAttendance(id, next),
    onSuccess: (_, { next }) => {
      refresh()
      setSelected((current) => current ? { ...current, attendanceStatus: next } : null)
      toast.success('참석 여부를 기록했습니다. CRM 접수함에도 반영됩니다.')
    },
    onError: (error) => toast.error(handleApiError(error) || '참석 여부 기록에 실패했습니다.'),
  })
  const download = useMutation({
    mutationFn: () => briefingApplicationApi.export(listParams),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    },
    onError: (error) => toast.error(handleApiError(error) || 'CSV 다운로드에 실패했습니다.'),
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <FilterSelect className="w-32" label="처리 상태" value={status || undefined} onChange={(next) => { setStatus(next as BriefingStatus || ''); setPage(0) }} items={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))} />
        <FilterSelect className="w-32" label="방식" value={sessionMode || undefined} onChange={(next) => { setSessionMode(next as SessionMode || ''); changeSession('') }} items={Object.entries(SESSION_MODE_LABELS).map(([value, label]) => ({ value, label }))} />
        <Field className="w-auto">
          <FieldLabel>회차 · 최신순</FieldLabel>
          <BriefingSessionFilter sessions={(sessions.data ?? []).filter((session) => !sessionMode || session.mode === sessionMode)} value={sessionId} onChange={changeSession} />
        </Field>
        <Field className="min-w-48 flex-1">
          <FieldLabel htmlFor="briefing-application-search">이름·전화번호</FieldLabel>
          <Input id="briefing-application-search" value={searchInput} placeholder="이름 또는 전화번호 검색" onChange={(event) => setSearchInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { setSearch(searchInput.trim()); setPage(0) } }} />
        </Field>
        <Button variant="outline" onClick={() => { setSearch(searchInput.trim()); setPage(0) }}>검색</Button>
        <Button variant="outline" disabled={download.isPending} onClick={() => download.mutate()}>{download.isPending ? <Spinner data-icon="inline-start" /> : <Download data-icon="inline-start" />}CSV 다운로드</Button>
      </div>
      {sessions.error && <ErrorBlock message="회차 목록을 불러오지 못했습니다." />}
      {currentSession && <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">신청 {currentSession.activeApplicationCount}명</Badge>
        <Badge>참석 {currentSession.attendedCount}명</Badge>
        <Badge variant="secondary">불참 {currentSession.noShowCount}명</Badge>
        <Badge variant="outline">미기록 {currentSession.unknownAttendanceCount}명</Badge>
        <Button onClick={() => setAttendanceOpen(true)}><Users data-icon="inline-start" />회차 출석부</Button>
      </div>}
      {!sessionId && <p className="text-sm text-muted-foreground">회차를 선택하면 당일·지난 설명회의 출석부와 참석 집계를 확인할 수 있습니다.</p>}
      {list.isLoading && <LoadingBlock />}
      {list.error && <ErrorBlock message="신청 목록을 불러오지 못했습니다." />}
      {list.data && <>
        <Table>
          <TableHeader><TableRow>
            {['신청일', '이름', '전화', '가맹 유형', '회차', '방식', '참석', '지역', '관심 항목', '처리 상태', '작업'].map((label) => <TableHead key={label}>{label}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {list.data.content.length === 0 && <TableRow><TableCell colSpan={11} className="h-24 text-center text-muted-foreground">접수된 신청이 없습니다.</TableCell></TableRow>}
            {list.data.content.map((row) => <TableRow key={row.id} className="cursor-pointer" onClick={() => setSelected(row)}>
              <TableCell className="whitespace-nowrap tabular-nums">{formatDate(row.createdAt)}</TableCell>
              <TableCell><Button variant="link" onClick={(event) => { event.stopPropagation(); setSelected(row) }}>{row.name}</Button></TableCell>
              <TableCell className="tabular-nums">{row.phone}</TableCell>
              <TableCell>{labels(row.franchiseTypes, FRANCHISE_TYPE_LABELS)}</TableCell>
              <TableCell className="whitespace-nowrap">{row.sessionDate}</TableCell>
              <TableCell>{SESSION_MODE_LABELS[row.sessionMode]}</TableCell>
              <TableCell><Badge variant={row.attendanceStatus === 'ATTENDED' ? 'default' : 'outline'}>{ATTENDANCE_LABEL[row.attendanceStatus]}</Badge>{row.participationStatus === 'cancelled' && <span className="ml-1 text-xs text-muted-foreground">신청 취소</span>}</TableCell>
              <TableCell>{[row.sido, row.sigungu].filter(Boolean).join(' ') || '-'}</TableCell>
              <TableCell>{labels(row.interests, INTEREST_LABELS)}</TableCell>
              <TableCell><Badge variant="secondary">{STATUS_LABEL[row.status]}</Badge></TableCell>
              <TableCell><Button size="sm" variant="ghost" onClick={(event) => { event.stopPropagation(); setSelected(row) }}>상세</Button></TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
        <Pagination page={list.data.page} totalPages={list.data.totalPages} totalElements={list.data.totalElements} onChange={setPage} />
      </>}
      {attendanceOpen && currentSession && <BriefingAttendanceDialog session={currentSession} onClose={() => setAttendanceOpen(false)} />}
      {selected && <DetailPanel key={selected.id} item={selected} openSessions={openSessions} onClose={() => setSelected(null)} onSave={(payload) => update.mutate({ id: selected.id, payload })} onChangeStatus={(next) => processing.mutate({ id: selected.id, next })} onChangeAttendance={(next) => attendance.mutate({ id: selected.id, next })} onMove={(targetSessionId) => participation.mutate({ id: selected.id, targetSessionId })} onCancel={() => participation.mutate({ id: selected.id })} busy={update.isPending || processing.isPending || attendance.isPending || participation.isPending} />}
    </div>
  )
}

function labels(values: string[], dictionary: Record<string, string>) {
  return values.map((value) => dictionary[value] ?? value).join(', ') || '-'
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(value))
}

function DetailPanel({ item, openSessions, onClose, onSave, onChangeStatus, onChangeAttendance, onMove, onCancel, busy }: {
  item: BriefingApplicationListItem
  openSessions: BriefingSessionAdmin[]
  onClose: () => void
  onSave: (payload: BriefingApplicationUpdatePayload) => void
  onChangeStatus: (next: BriefingStatus) => void
  onChangeAttendance: (next: BriefingAttendanceStatus) => void
  onMove: (sessionId: string) => void
  onCancel: () => void
  busy: boolean
}) {
  const [name, setName] = useState(item.name)
  const [phone, setPhone] = useState(item.phone)
  const [franchiseTypes, setFranchiseTypes] = useState(item.franchiseTypes)
  const [sido, setSido] = useState(item.sido ?? '')
  const [sigungu, setSigungu] = useState(item.sigungu ?? '')
  const [interests, setInterests] = useState(item.interests)
  const [otherText, setOtherText] = useState(item.otherText ?? '')
  const [marketingConsent, setMarketingConsent] = useState(item.marketingConsent)
  const [targetSessionId, setTargetSessionId] = useState('')
  const toggle = (values: string[], value: string) => values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value]
  const submit = () => onSave({
    name: name.trim(), phone: phone.trim(), franchiseTypes, sido: sido || null, sigungu: sigungu || null,
    interests, otherText: interests.includes('other') ? otherText.trim() || null : null, marketingConsent,
  })

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose() }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader><DialogTitle>사업설명회 신청 상세</DialogTitle><DialogDescription>신청 정보를 수정하고 참석 신청·실제 참석 여부를 관리합니다.</DialogDescription></DialogHeader>
        <FieldGroup>
          <Definition label="신청일">{formatDate(item.createdAt)}</Definition>
          <Field><FieldLabel htmlFor="briefing-name">이름</FieldLabel><Input id="briefing-name" value={name} onChange={(event) => setName(event.target.value)} /></Field>
          <Field><FieldLabel htmlFor="briefing-phone">전화</FieldLabel><Input id="briefing-phone" value={phone} inputMode="tel" onChange={(event) => setPhone(event.target.value)} /></Field>
          <Definition label="회차">{item.sessionDate} · {SESSION_MODE_LABELS[item.sessionMode]}</Definition>
          <FieldSet>
            <FieldLegend variant="label">참석 신청 · {item.participationStatus === 'active' ? '활성' : '취소'}</FieldLegend>
            <div className="flex items-end gap-2">
              <Field className="flex-1"><FieldLabel>이동할 회차</FieldLabel><LabeledSelect ariaLabel="이동할 회차" value={targetSessionId || 'unset'} onChange={(next) => setTargetSessionId(next === 'unset' ? '' : next)} items={[{ value: 'unset', label: '회차 선택' }, ...openSessions.filter((session) => session.sessionId !== item.sessionId).map((session) => ({ value: session.sessionId, label: `${formatDate(session.startsAt)} · ${SESSION_MODE_LABELS[session.mode]}` }))]} /></Field>
              <Button variant="outline" disabled={!targetSessionId || busy} onClick={() => onMove(targetSessionId)}>회차 이동</Button>
            </div>
            <Button variant="destructive" disabled={item.participationStatus === 'cancelled' || busy} onClick={() => { if (window.confirm('참석 신청을 취소할까요?')) onCancel() }}>참석 신청 취소</Button>
          </FieldSet>
          <Field><FieldLabel>설명회 참석 · 선택 즉시 저장</FieldLabel><LabeledSelect ariaLabel="설명회 참석" value={item.attendanceStatus} items={Object.entries(ATTENDANCE_LABEL).filter(([value]) => item.participationStatus !== 'cancelled' || value === 'UNKNOWN' || value === item.attendanceStatus).map(([value, label]) => ({ value, label }))} onChange={(next) => { if (!busy && (next === 'ATTENDED' || next === 'NO_SHOW' || next === 'UNKNOWN')) onChangeAttendance(next) }} /></Field>
          <ChoiceSet title="가맹 유형" values={franchiseTypes} labels={FRANCHISE_TYPE_LABELS} onToggle={(value) => setFranchiseTypes((current) => toggle(current, value))} />
          <Field><FieldLabel>지역 (시/도 · 시/군/구)</FieldLabel><RegionPicker sido={sido} sigungu={sigungu} onChange={(next) => { setSido(next.sido ?? ''); setSigungu(next.sigungu ?? '') }} /></Field>
          <ChoiceSet title="관심 항목" values={interests} labels={INTEREST_LABELS} onToggle={(value) => setInterests((current) => toggle(current, value))} />
          {interests.includes('other') && <Field><FieldLabel htmlFor="briefing-other">기타 입력</FieldLabel><Textarea id="briefing-other" value={otherText} onChange={(event) => setOtherText(event.target.value)} /></Field>}
          <Definition label="개인정보 수집·이용 동의">{item.privacyConsentedAt ? `동의 (${formatDate(item.privacyConsentedAt)})` : '미동의'}</Definition>
          <Field orientation="horizontal"><Checkbox id="briefing-marketing" checked={marketingConsent} onCheckedChange={setMarketingConsent} /><FieldLabel htmlFor="briefing-marketing">마케팅 정보 수신 동의</FieldLabel></Field>
          {item.marketingConsentedAt && <p className="text-xs text-muted-foreground">최초 동의: {formatDate(item.marketingConsentedAt)}</p>}
          <Field><FieldLabel>처리 상태 · 선택 즉시 저장</FieldLabel><LabeledSelect ariaLabel="신청 처리 상태" value={item.status} items={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))} onChange={(next) => { if (!busy) onChangeStatus(next as BriefingStatus) }} /></Field>
        </FieldGroup>
        <DialogFooter><Button variant="outline" disabled={busy} onClick={onClose}>닫기</Button><Button disabled={busy || !name.trim() || !phone.trim()} onClick={submit}>{busy && <Spinner data-icon="inline-start" />}변경사항 저장</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ChoiceSet({ title, values, labels: dictionary, onToggle }: { title: string; values: string[]; labels: Record<string, string>; onToggle: (value: string) => void }) {
  return <FieldSet><FieldLegend variant="label">{title}</FieldLegend><FieldGroup className="gap-2">{Object.entries(dictionary).map(([value, label]) => <Field key={value} orientation="horizontal"><Checkbox id={`briefing-choice-${value}`} checked={values.includes(value)} onCheckedChange={() => onToggle(value)} /><FieldLabel htmlFor={`briefing-choice-${value}`}>{label}</FieldLabel></Field>)}</FieldGroup></FieldSet>
}

function Definition({ label, children }: { label: string; children: ReactNode }) {
  return <div className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">{label}</span><span className="text-sm">{children}</span></div>
}

function RegionPicker({ sido, sigungu, onChange }: { sido?: string; sigungu?: string; onChange: (next: { sido?: string; sigungu?: string }) => void }) {
  const sidoQuery = useSidoList()
  const sidoList = sidoQuery.data ?? []
  const entry = sidoList.find((region) => region.name === sido)
  const sigunguQuery = useSigunguList(entry?.code)
  const sigunguList: RegcodeEntry[] = sigunguQuery.data ?? []
  const sidoOptions = sido && !sidoList.some((region) => region.name === sido) ? [...sidoList, { code: 'stored-sido', name: sido }] : sidoList
  const sigunguOptions = sigungu && !sigunguList.some((region) => region.name === sigungu) ? [...sigunguList, { code: 'stored-sigungu', name: sigungu }] : sigunguList
  return <div className="grid grid-cols-2 gap-2">
    <LabeledSelect ariaLabel="시/도" value={sido || 'unset'} items={[{ value: 'unset', label: '시/도 선택' }, ...sidoOptions.map((region) => ({ value: region.name, label: region.name }))]} onChange={(next) => onChange({ sido: next === 'unset' ? undefined : next, sigungu: undefined })} />
    {sigunguQuery.isError
      ? <Input aria-label="시/군/구" value={sigungu ?? ''} disabled={!sido} placeholder="시/군/구" onChange={(event) => onChange({ sido, sigungu: event.target.value || undefined })} />
      : <LabeledSelect ariaLabel="시/군/구" value={sigungu || 'unset'} items={[{ value: 'unset', label: sigunguQuery.isLoading ? '불러오는 중' : '시/군/구 선택' }, ...sigunguOptions.map((region) => ({ value: region.name, label: region.name }))]} onChange={(next) => onChange({ sido, sigungu: next === 'unset' ? undefined : next })} />}
  </div>
}
